/**
 * Utilities for handling local food photos with attribution
 */

interface PhotoData {
  foodId: string;
  database: string;
  foodName: string;
  foodNameDE: string;
  attribution: string;
  author: string;
  source: string;
  photoUrl: string;
  directPhotoUrl?: string;
  photographerUrl?: string;
  photographerProfileUrl?: string;
  attributionHtml?: string;
  title: string;
  sizes: {
    [size: string]: string;
  };
  downloadDate: string;
}

interface PhotoIndex {
  version: string;
  lastUpdated: string;
  description: string;
  totalPhotos: number;
  photos: {
    [foodId: string]: PhotoData;
  };
}

// Cache for photo index
let photoIndexCache: PhotoIndex | null = null;
let photoIndexPromise: Promise<PhotoIndex | null> | null = null;

/**
 * Load photo index from JSON file
 */
export const loadPhotoIndex = async (): Promise<PhotoIndex | null> => {
  // Return cached version if available
  if (photoIndexCache) {
    return photoIndexCache;
  }

  // Return existing promise if already loading
  if (photoIndexPromise) {
    return photoIndexPromise;
  }

  photoIndexPromise = fetch('/photo_index.json')
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load photo index: ${response.status}`);
      }
      return response.json();
    })
    .then((data: PhotoIndex) => {
      photoIndexCache = data;
      return data;
    })
    .catch(error => {
      console.log('Photo index not available:', error.message);
      return null;
    });

  return photoIndexPromise;
};

/**
 * Get photo data for a specific food ID
 */
export const getPhotoData = async (foodId: string): Promise<PhotoData | null> => {
  const index = await loadPhotoIndex();
  return index?.photos[foodId] || null;
};

/**
 * Get local food image URL
 */
export const getLocalFoodImageUrl = (
  foodId: string,
  size: '100' | '200' | '400' = '200'
): string | null => {
  // Check if photo exists for this food ID
  // This is a synchronous check - the actual photo loading will be handled by the component
  return `/food_images/${getDatabaseFromFoodId(foodId)}/${foodId}.${size}.jpg`;
};

/**
 * Check if a local photo exists for a food ID
 */
export const hasLocalPhoto = async (foodId: string): Promise<boolean> => {
  const photoData = await getPhotoData(foodId);
  return photoData !== null;
};

/**
 * Get attribution text for a photo
 */
export const getPhotoAttribution = async (foodId: string): Promise<string | null> => {
  const photoData = await getPhotoData(foodId);
  return photoData?.attribution || null;
};

/**
 * Extract database name from food ID
 */
const getDatabaseFromFoodId = (foodId: string): string => {
  if (foodId.startsWith('swiss_')) {
    return 'swiss';
  } else if (foodId.startsWith('usda_')) {
    return 'usda';
  } else if (foodId.startsWith('off_')) {
    return 'openfoodfacts';
  }
  return 'unknown';
};

/**
 * Format attribution for display
 */
export const formatAttribution = (photoData: PhotoData): string => {
  return photoData.attribution;
};

/**
 * Get photo statistics
 */
export const getPhotoStats = async (): Promise<{
  totalPhotos: number;
  byDatabase: { [db: string]: number };
} | null> => {
  const index = await loadPhotoIndex();
  if (!index) return null;

  const byDatabase: { [db: string]: number } = {};
  
  Object.values(index.photos).forEach(photo => {
    byDatabase[photo.database] = (byDatabase[photo.database] || 0) + 1;
  });

  return {
    totalPhotos: index.totalPhotos,
    byDatabase
  };
};